import { PlusOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Col, Row, Space, Typography } from 'antd';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormNumberInput from 'components/new-ui/composed/formInput/FormNumberInput';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import IconTooltip from 'components/ui/base/tooltip/IconTooltip';
import { FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';
import { SubmitInvoiceChargeFormFields } from 'views/invoices/types';
import { invoiceChargeSchema } from '../validationSchemas';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import { AdditionalCharge, IRELAND_DEFAULT_TAX_CODES } from 'store/invoice-config/types';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import { createAdditionalCharge, editAdditionalCharge } from 'store/invoice-config/client';
import { toAdditionalChargePayload } from 'views/invoices/utils';
import { SubError } from 'core/http/response';

type Props = {
    open?: boolean;
    selectedCharge?: AdditionalCharge;
    onClose?: () => void;
    onSubmit?: () => void;
    currencySymbol: string;
};

enum FormFields {
    PRODUCT_CODE = 'productCode',
    DESCRIPTION = 'description',
    NOMINAL_CODE = 'nominalCode',
    TAX_CODE = 'taxCode',
    AMOUNT = 'amount',
    PERCENTAGE = 'percentage',
}

const AdditionalChargesModal = ({ open, selectedCharge, currencySymbol, onClose, onSubmit }: Props) => {
    const title = selectedCharge ? 'Edit Additional Charge' : 'Create Additional Charge';
    const icon = selectedCharge ? <EditOutlined /> : <PlusOutlined />;
    const confirmButtonText = selectedCharge ? 'Save' : 'Create';

    const taxCodeOptions: SelectOption[] = IRELAND_DEFAULT_TAX_CODES.map((r) => ({
        id: r.code,
        value: r.label,
    }));

    const initialValues: SubmitInvoiceChargeFormFields = useMemo(() => {
        return {
            amount: selectedCharge?.amount ?? null,
            description: selectedCharge?.description ?? '',
            nominalCode: selectedCharge?.nominalCode ?? '',
            productCode: selectedCharge?.productCode ?? '',
            taxCode: selectedCharge?.taxCode ?? '',
            percentage: selectedCharge?.percentage ?? null,
        };
    }, [selectedCharge]);

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: invoiceChargeSchema,
        onSubmit: async (values, helpers) => {
            try {
                const payload = toAdditionalChargePayload(values);

                if (selectedCharge?.id) {
                    await editAdditionalCharge(selectedCharge.id, payload);
                } else {
                    await createAdditionalCharge(payload);
                }

                Notification({
                    type: 'success',
                    messageTitle: 'Operation Successful',
                    description: selectedCharge?.id
                        ? 'Additional charge updated successfully.'
                        : 'Additional charge added successfully.',
                    placement: 'top',
                });

                helpers.resetForm();
                onSubmit?.();
            } catch (err: unknown) {
                const data = (err as any)?.response?.data ?? err;
                const subErrors = Array.isArray(data?.subErrors) ? data.subErrors : [];

                if (subErrors.length > 0) {
                    for (const subErr of subErrors as SubError[]) {
                        const fieldPath = subErr.field;
                        if (!fieldPath) continue;

                        const message = subErr.message || data?.message || 'Invalid value';

                        formik.setFieldTouched(fieldPath, true, false);
                        formik.setFieldError(fieldPath, message);
                    }

                    Notification({
                        type: 'error',
                        messageTitle: 'Operation Failed',
                        description: data?.message || 'Some fields are invalid. Please fix the highlighted fields.',
                        placement: 'top',
                    });

                    return;
                }

                Notification({
                    type: 'error',
                    messageTitle: 'Operation Failed',
                    description: getErrorMessage(err) || 'Could not create additional charge. Please try again later.',
                    placement: 'top',
                });
            }
        },
    });

    const handleCancel = () => {
        formik.resetForm();
        onClose?.();
    };

    return (
        <ConfirmationModal
            destroyOnClose
            width={600}
            open={open}
            title={title}
            icon={icon}
            confirmationButtonDisabled={!(formik.isValid && formik.dirty)}
            confirmButtonText={confirmButtonText}
            confirmationButtonLoading={formik.isSubmitting}
            onConfirm={formik.submitForm}
            onCancel={handleCancel}
            content={
                <FormikProvider value={formik}>
                    <Space style={{ width: '100%' }} direction="vertical" size={16}>
                        <Row>
                            <Typography.Text>
                                These fields map directly to Sage's audit trail transaction import structure. To prevent
                                any discrepancies ensure the values match.
                            </Typography.Text>
                        </Row>

                        <Row justify="space-between" gutter={6} align="middle">
                            <Col span={10}>
                                <Space size={4}>
                                    <LabelRequiredSymbol />
                                    <label htmlFor={FormFields.PRODUCT_CODE}>Product Code</label>
                                    <IconTooltip
                                        title="Product Code"
                                        tooltip="Unique identifier for the additional charge."
                                        icon={<InfoCircleOutlined />}
                                    />
                                    :
                                </Space>
                            </Col>
                            <Col span={14}>
                                <FormInput
                                    placeholder="Enter product code"
                                    fieldProps={formik.getFieldProps(FormFields.PRODUCT_CODE)}
                                    fieldHelper={formik.getFieldHelpers(FormFields.PRODUCT_CODE)}
                                    fieldMeta={formik.getFieldMeta(FormFields.PRODUCT_CODE)}
                                />
                            </Col>
                        </Row>

                        <Row justify="space-between" gutter={6} align="middle">
                            <Col span={10}>
                                <Space size={4}>
                                    <LabelRequiredSymbol />
                                    <label htmlFor={FormFields.DESCRIPTION}>Description</label>
                                    <IconTooltip
                                        title="Description"
                                        tooltip="Clear description of the additional charge."
                                        icon={<InfoCircleOutlined />}
                                    />
                                    :
                                </Space>
                            </Col>
                            <Col span={14}>
                                <FormInput
                                    placeholder="Enter description"
                                    fieldProps={formik.getFieldProps(FormFields.DESCRIPTION)}
                                    fieldHelper={formik.getFieldHelpers(FormFields.DESCRIPTION)}
                                    fieldMeta={formik.getFieldMeta(FormFields.DESCRIPTION)}
                                />
                            </Col>
                        </Row>

                        <Row justify="space-between" gutter={6} align="middle">
                            <Col span={10}>
                                <Space size={4}>
                                    <LabelRequiredSymbol />
                                    <label htmlFor={FormFields.NOMINAL_CODE}>Nominal Code</label>
                                    <IconTooltip
                                        title="Nominal Code"
                                        tooltip="Must be a valid Sage code with digits only."
                                        icon={<InfoCircleOutlined />}
                                    />
                                    :
                                </Space>
                            </Col>
                            <Col span={14}>
                                <FormInput
                                    placeholder="Enter Nominal Code"
                                    fieldProps={formik.getFieldProps(FormFields.NOMINAL_CODE)}
                                    fieldHelper={formik.getFieldHelpers(FormFields.NOMINAL_CODE)}
                                    fieldMeta={formik.getFieldMeta(FormFields.NOMINAL_CODE)}
                                />
                            </Col>
                        </Row>

                        <Row justify="space-between" gutter={6} align="middle">
                            <Col span={10}>
                                <Space size={4}>
                                    <LabelRequiredSymbol />
                                    <label htmlFor={FormFields.TAX_CODE}>Tax Code</label>
                                    <IconTooltip
                                        title="Tax Code"
                                        tooltip="Must match a valid Sage tax code."
                                        icon={<InfoCircleOutlined />}
                                    />
                                    :
                                </Space>
                            </Col>
                            <Col span={14}>
                                <FormSelect
                                    hideKeys
                                    placeholder="Select Tax Code"
                                    fieldHelper={formik.getFieldHelpers(FormFields.TAX_CODE)}
                                    fieldProps={formik.getFieldProps(FormFields.TAX_CODE)}
                                    fieldMeta={formik.getFieldMeta(FormFields.TAX_CODE)}
                                    selectOptions={taxCodeOptions}
                                />
                            </Col>
                        </Row>

                        <Row justify="space-between" gutter={6} align="middle">
                            <Col span={10}>
                                <Space size={4}>
                                    <LabelRequiredSymbol />
                                    <label htmlFor={FormFields.AMOUNT}>Amount ({currencySymbol})</label>
                                    <IconTooltip
                                        title="Amount"
                                        tooltip="Amount for this line item."
                                        icon={<InfoCircleOutlined />}
                                    />
                                    :
                                </Space>
                            </Col>
                            <Col span={14}>
                                <FormNumberInput
                                    placeholder="Enter Amount"
                                    name={FormFields.AMOUNT}
                                    precision={2}
                                    min={0}
                                />
                            </Col>
                        </Row>

                        <Row justify="space-between" gutter={6} align="middle">
                            <Col span={10}>
                                <Space size={4}>
                                    <label htmlFor={FormFields.PERCENTAGE}>Percentage (%)</label>
                                    <IconTooltip
                                        title="Percentage"
                                        tooltip="Enter the percentage value that applies to this charge."
                                        icon={<InfoCircleOutlined />}
                                    />
                                    :
                                </Space>
                            </Col>
                            <Col span={14}>
                                <FormNumberInput
                                    placeholder="Enter Percentage"
                                    name={FormFields.PERCENTAGE}
                                    precision={2}
                                    min={0}
                                />
                            </Col>
                        </Row>
                    </Space>
                </FormikProvider>
            }
        />
    );
};

export default AdditionalChargesModal;
